/**
 * ComposerSettingsDialog — manage the account's signatures, templates, and
 * snippets (Vault-backed). Compact create/list/delete per section; signatures
 * support a default toggle. Editing is delete + recreate in v1.
 */

import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Trash2, Star, Plus, Pencil, Loader2, UserCog, AtSign } from 'lucide-react';
import {
  useEmailSignatures,
  useCreateSignature,
  useUpdateSignature,
  useDeleteSignature,
  useEmailTemplates,
  useCreateTemplate,
  useDeleteTemplate,
  useEmailSnippets,
  useCreateSnippet,
  useDeleteSnippet,
} from '../hooks/useComposerAssets';
import { FolderRolesSection } from './FolderRolesSection';
import { useDialogAccount } from '../hooks/useDialogAccount';
import { useSetEmailFeatures } from '../hooks/useEmailFeatures';
import {
  useEmailUserSettings,
  useSetEmailUserSettings,
  EMAIL_UNDO_SEND_MIN_SECONDS,
  EMAIL_UNDO_SEND_MAX_SECONDS,
} from '../hooks/useEmailUserSettings';
import type { EmailReplyQuoteOrderDto, EmailUserSettingsDto } from '@/lib/api';
import { useSwipePrefs, SWIPE_ACTION_LABELS, type SwipeAction } from '../hooks/useMobile';
import {
  useEmailListPrefs,
  PREVIEW_LINE_OPTIONS,
  type PreviewLines,
} from '../hooks/useEmailListPrefs';

interface ComposerSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId?: string;
}

export function ComposerSettingsDialog({
  open,
  onOpenChange,
  accountId,
}: ComposerSettingsDialogProps) {
  // The per-account sections (folders, signatures, templates, snippets) operate
  // on one chosen account, picked here in the dialog. Global settings above
  // apply to every account. Seeded from the passed account, else primary/first.
  const { accounts, selectedAccountId, setSelectedAccountId } = useDialogAccount(accountId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Email settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {/* Global settings — apply across every account and device. */}
          <MessageListSection />
          <MobileSwipeSection />
          <FeaturesSection />
          <ComposingSection />

          {/* Per-account settings — scoped to the account chosen in the picker.
              Set apart from the global sections above (its own tinted card +
              a prominent, primary-accented account picker) because it was easy
              to miss that these sections are per-account and that the pulldown
              switches which account you're editing. */}
          <section className="space-y-3 rounded-lg border bg-muted/30 p-4">
            <div className="space-y-1">
              <h3 className="flex items-center gap-1.5 text-sm font-semibold">
                <UserCog className="h-4 w-4 shrink-0" />
                Per-account settings
              </h3>
              <p className="text-xs text-muted-foreground">
                Folders, signatures, templates, and snippets are saved separately for each account.
                Pick the account these apply to.
              </p>
            </div>
            {accounts.length === 0 ? (
              <p className="py-1 text-sm text-muted-foreground">
                Connect an account to manage folders, signatures, templates, and snippets.
              </p>
            ) : (
              <>
                <div className="space-y-1 rounded-md border border-primary/30 bg-background p-3">
                  <label
                    className="flex items-center gap-1.5 text-xs font-medium"
                    htmlFor="settings-account"
                  >
                    <AtSign className="h-3.5 w-3.5 text-primary" />
                    Editing settings for
                  </label>
                  <select
                    id="settings-account"
                    aria-label="Account for these settings"
                    className="h-10 w-full rounded-md border bg-background px-2 text-sm font-medium"
                    value={selectedAccountId ?? ''}
                    onChange={(e) => setSelectedAccountId(e.target.value)}
                  >
                    {accounts.map((a) => (
                      <option key={a.accountId} value={a.accountId}>
                        {a.emailAddress}
                      </option>
                    ))}
                  </select>
                  {accounts.length > 1 && (
                    <p className="text-xs text-muted-foreground">
                      You have {accounts.length} connected accounts — switch here to edit another.
                    </p>
                  )}
                </div>
                {selectedAccountId && (
                  <div className="space-y-6">
                    <FolderRolesSection accountId={selectedAccountId} />
                    <SignaturesSection accountId={selectedAccountId} />
                    <TemplatesSection accountId={selectedAccountId} />
                    <SnippetsSection accountId={selectedAccountId} />
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SectionHeader({ title }: { title: string }) {
  return <h3 className="text-sm font-semibold border-b pb-1">{title}</h3>;
}

/**
 * Optional-feature opt-ins — HOLDER-WIDE (global across all accounts), persisted
 * server-side. Off by default so Snooze / Scheduled send don't clutter the UI
 * unless wanted.
 */
function FeaturesSection() {
  const { scheduleEnabled, snoozeEnabled } = useEmailUserSettings().features;
  const setFeatures = useSetEmailFeatures();
  return (
    <section className="space-y-2">
      <SectionHeader title="Features" />
      <p className="text-xs text-muted-foreground">
        Turn optional tools on to keep the interface uncluttered until you need them.
      </p>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-1"
          checked={scheduleEnabled}
          disabled={setFeatures.isPending}
          onChange={(e) => setFeatures.mutate({ scheduleEnabled: e.target.checked })}
        />
        <span>
          Scheduled send
          <span className="block text-xs text-muted-foreground">
            Shows the Schedule option in the composer and a Scheduled list in the sidebar.
          </span>
        </span>
      </label>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-1"
          checked={snoozeEnabled}
          disabled={setFeatures.isPending}
          onChange={(e) => setFeatures.mutate({ snoozeEnabled: e.target.checked })}
        />
        <span>
          Snooze
          <span className="block text-xs text-muted-foreground">
            Shows the Snooze action on messages and a Snoozed list in the sidebar.
          </span>
        </span>
      </label>
    </section>
  );
}

/**
 * Composing preferences — HOLDER-WIDE. Undo-send window (off by default),
 * auto-insert default signature (off by default), and reply quote placement
 * (inline-above by default).
 */
function ComposingSection() {
  const settings = useEmailUserSettings();
  const setAll = useSetEmailUserSettings();
  const patch = (p: Partial<EmailUserSettingsDto>) => setAll.mutate({ ...settings, ...p });
  const clampSeconds = (raw: number) =>
    Math.min(EMAIL_UNDO_SEND_MAX_SECONDS, Math.max(EMAIL_UNDO_SEND_MIN_SECONDS, raw));
  return (
    <section className="space-y-3">
      <SectionHeader title="Composing" />
      <div className="space-y-1">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={settings.undoSend.enabled}
            onChange={(e) =>
              patch({ undoSend: { ...settings.undoSend, enabled: e.target.checked } })
            }
          />
          <span>
            Undo send
            <span className="block text-xs text-muted-foreground">
              Hold a message briefly after Send so you can cancel it.
            </span>
          </span>
        </label>
        {settings.undoSend.enabled && (
          <div className="flex items-center gap-1 pl-6 text-sm">
            Cancel window
            <input
              type="number"
              min={EMAIL_UNDO_SEND_MIN_SECONDS}
              max={EMAIL_UNDO_SEND_MAX_SECONDS}
              value={settings.undoSend.seconds}
              aria-label="Undo send window in seconds"
              className="w-16 rounded border px-1 py-0.5 text-sm"
              onChange={(e) =>
                patch({
                  undoSend: {
                    ...settings.undoSend,
                    seconds: clampSeconds(Number(e.target.value) || EMAIL_UNDO_SEND_MIN_SECONDS),
                  },
                })
              }
            />
            seconds
          </div>
        )}
      </div>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-1"
          checked={settings.autoSignature}
          onChange={(e) => patch({ autoSignature: e.target.checked })}
        />
        <span>
          Auto-insert signature
          <span className="block text-xs text-muted-foreground">
            Add your default signature to new messages and replies.
          </span>
        </span>
      </label>
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground" htmlFor="reply-quote-order">
          When replying or forwarding, place my message
        </label>
        <select
          id="reply-quote-order"
          className="h-9 w-full rounded-md border bg-background px-2 text-sm"
          value={settings.replyQuoteOrder}
          onChange={(e) => patch({ replyQuoteOrder: e.target.value as EmailReplyQuoteOrderDto })}
        >
          <option value="above">Above the quoted message (inline)</option>
          <option value="below">Below the quoted message</option>
          <option value="none">Don’t quote the original</option>
        </select>
      </div>
    </section>
  );
}

/**
 * Message-list display — holder-wide (server-stored, cross-account/device). How
 * many lines of the message preview snippet to show per row.
 */
function MessageListSection() {
  const [prefs, setPrefs] = useEmailListPrefs();
  const settings = useEmailUserSettings();
  const setAll = useSetEmailUserSettings();
  return (
    <section className="space-y-2">
      <SectionHeader title="Message list" />
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground" htmlFor="preview-lines">
          Preview lines
        </label>
        <select
          id="preview-lines"
          className="h-9 w-full rounded-md border bg-background px-2 text-sm"
          value={prefs.previewLines}
          onChange={(e) => setPrefs({ previewLines: Number(e.target.value) as PreviewLines })}
        >
          {PREVIEW_LINE_OPTIONS.map((n) => (
            <option key={n} value={n}>
              {n === 1 ? '1 line' : `${n} lines`}
            </option>
          ))}
        </select>
      </div>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-1"
          checked={settings.conversationThreadingEnabled}
          disabled={setAll.isPending}
          onChange={(e) =>
            setAll.mutate({ ...settings, conversationThreadingEnabled: e.target.checked })
          }
        />
        <span>
          Conversation view
          <span className="block text-xs text-muted-foreground">
            Group messages in the same conversation into a single expandable row.
          </span>
        </span>
      </label>
    </section>
  );
}

const SWIPE_OPTIONS: SwipeAction[] = ['none', 'trash', 'archive', 'read', 'star'];

/**
 * Mobile swipe gestures — holder-wide (server-stored, cross-account/device).
 * Swipe a message-list row left or right to run the chosen action; defaults are
 * left = Trash, right = Archive.
 */
function MobileSwipeSection() {
  const [prefs, setPrefs] = useSwipePrefs();
  return (
    <section className="space-y-2">
      <SectionHeader title="Mobile swipe gestures" />
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={prefs.enabled}
          onChange={(e) => setPrefs({ enabled: e.target.checked })}
        />
        Enable swipe actions on touch devices
      </label>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground" htmlFor="swipe-left">
            Swipe left
          </label>
          <select
            id="swipe-left"
            className="h-9 w-full rounded-md border bg-background px-2 text-sm disabled:opacity-50"
            value={prefs.left}
            disabled={!prefs.enabled}
            onChange={(e) => setPrefs({ left: e.target.value as SwipeAction })}
          >
            {SWIPE_OPTIONS.map((a) => (
              <option key={a} value={a}>
                {SWIPE_ACTION_LABELS[a]}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground" htmlFor="swipe-right">
            Swipe right
          </label>
          <select
            id="swipe-right"
            className="h-9 w-full rounded-md border bg-background px-2 text-sm disabled:opacity-50"
            value={prefs.right}
            disabled={!prefs.enabled}
            onChange={(e) => setPrefs({ right: e.target.value as SwipeAction })}
          >
            {SWIPE_OPTIONS.map((a) => (
              <option key={a} value={a}>
                {SWIPE_ACTION_LABELS[a]}
              </option>
            ))}
          </select>
        </div>
      </div>
    </section>
  );
}

function SignaturesSection({ accountId }: { accountId: string }) {
  const { data: signatures, isLoading } = useEmailSignatures(accountId);
  const create = useCreateSignature(accountId);
  const update = useUpdateSignature(accountId);
  const del = useDeleteSignature(accountId);
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  // When set, the form edits an existing signature (name + body) rather than
  // creating one — this is also how you view a signature's body.
  const [editingId, setEditingId] = useState<string | null>(null);

  const resetForm = () => {
    setName('');
    setBody('');
    setEditingId(null);
  };

  const submit = () => {
    if (!name.trim() || !body.trim()) return;
    if (editingId) {
      update.mutate(
        { signatureId: editingId, name: name.trim(), bodyHtml: body },
        { onSuccess: resetForm },
      );
    } else {
      create.mutate(
        { name: name.trim(), bodyHtml: body, isDefault: (signatures?.length ?? 0) === 0 },
        { onSuccess: resetForm },
      );
    }
  };

  return (
    <section className="space-y-2">
      <SectionHeader title="Signatures" />
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : (
        <ul className="space-y-1">
          {(signatures ?? []).map((s) => (
            <li
              key={s.signatureId}
              className="flex items-center justify-between gap-2 rounded border px-2 py-1 text-sm"
            >
              <span className="flex items-center gap-1.5 truncate">
                {s.isDefault && <Star className="h-3.5 w-3.5 fill-current text-yellow-500" />}
                <span className="truncate font-medium">{s.name}</span>
              </span>
              <span className="flex items-center gap-1">
                {!s.isDefault && (
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Set default"
                    onClick={() => update.mutate({ signatureId: s.signatureId, isDefault: true })}
                  >
                    <Star className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  title="Edit"
                  onClick={() => {
                    setEditingId(s.signatureId);
                    setName(s.name);
                    setBody(s.bodyHtml);
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  title="Delete"
                  onClick={() => {
                    if (editingId === s.signatureId) resetForm();
                    del.mutate(s.signatureId);
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="space-y-1">
        <Input
          placeholder="Signature name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Textarea
          placeholder="Signature text"
          rows={2}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            className="gap-1"
            disabled={!name.trim() || !body.trim() || create.isPending || update.isPending}
            onClick={submit}
          >
            {editingId ? (
              <>
                <Pencil className="h-3.5 w-3.5" /> Save signature
              </>
            ) : (
              <>
                <Plus className="h-3.5 w-3.5" /> Add signature
              </>
            )}
          </Button>
          {editingId && (
            <Button variant="ghost" size="sm" onClick={resetForm}>
              Cancel
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

function TemplatesSection({ accountId }: { accountId: string }) {
  const { data: templates, isLoading } = useEmailTemplates(accountId);
  const create = useCreateTemplate(accountId);
  const del = useDeleteTemplate(accountId);
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const add = () => {
    if (!name.trim() || !body.trim()) return;
    create.mutate(
      { name: name.trim(), subject: subject || null, bodyHtml: body },
      {
        onSuccess: () => {
          setName('');
          setSubject('');
          setBody('');
        },
      },
    );
  };

  return (
    <section className="space-y-2">
      <SectionHeader title="Templates" />
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : (
        <ul className="space-y-1">
          {(templates ?? []).map((t) => (
            <li
              key={t.templateId}
              className="flex items-center justify-between gap-2 rounded border px-2 py-1 text-sm"
            >
              <span className="truncate font-medium">{t.name}</span>
              <Button
                variant="ghost"
                size="sm"
                title="Delete"
                onClick={() => del.mutate(t.templateId)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="space-y-1">
        <Input placeholder="Template name" value={name} onChange={(e) => setName(e.target.value)} />
        <Input
          placeholder="Subject (optional)"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
        <Textarea
          placeholder="Body"
          rows={2}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <Button
          size="sm"
          className="gap-1"
          disabled={!name.trim() || !body.trim() || create.isPending}
          onClick={add}
        >
          <Plus className="h-3.5 w-3.5" /> Add template
        </Button>
      </div>
    </section>
  );
}

function SnippetsSection({ accountId }: { accountId: string }) {
  const { data: snippets, isLoading } = useEmailSnippets(accountId);
  const create = useCreateSnippet(accountId);
  const del = useDeleteSnippet(accountId);
  const [trigger, setTrigger] = useState('');
  const [body, setBody] = useState('');

  const add = () => {
    if (!trigger.trim() || !body.trim()) return;
    create.mutate(
      { trigger: trigger.trim(), body },
      {
        onSuccess: () => {
          setTrigger('');
          setBody('');
        },
      },
    );
  };

  return (
    <section className="space-y-2">
      <SectionHeader title="Snippets" />
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : (
        <ul className="space-y-1">
          {(snippets ?? []).map((s) => (
            <li
              key={s.snippetId}
              className="flex items-center justify-between gap-2 rounded border px-2 py-1 text-sm"
            >
              <span className="truncate">
                <span className="font-mono font-medium">{s.trigger}</span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                title="Delete"
                onClick={() => del.mutate(s.snippetId)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="space-y-1">
        <Input
          placeholder="Trigger (e.g. /thanks)"
          value={trigger}
          onChange={(e) => setTrigger(e.target.value)}
        />
        <Textarea
          placeholder="Expansion text"
          rows={2}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <Button
          size="sm"
          className="gap-1"
          disabled={!trigger.trim() || !body.trim() || create.isPending}
          onClick={add}
        >
          <Plus className="h-3.5 w-3.5" /> Add snippet
        </Button>
      </div>
    </section>
  );
}
